import { useState } from 'react';

export default function Gallery() {
  const [sliderPos, setSliderPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = (clientX: number, rect: DOMRect) => {
    const x = clientX - rect.left;
    const pct = Math.min(Math.max((x / rect.width) * 100, 2), 98);
    setSliderPos(pct);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    handleMove(e.clientX, e.currentTarget.getBoundingClientRect());
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    handleMove(e.touches[0].clientX, e.currentTarget.getBoundingClientRect());
  };

  const workPhotos = [
    { src: '/work1.jpg', caption: 'Driveway sealcoating in progress — Walworth County' },
    { src: '/work2.jpg', caption: 'Freshly sealed long driveway — beautiful results' },
  ];

  return (
    <section id="gallery" className="py-24 px-6 bg-black/30">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16">
          <div className="text-orange-500 font-display font-bold text-lg uppercase tracking-widest mb-3">Our Work</div>
          <h2 className="font-display text-6xl md:text-7xl font-bold text-white uppercase mb-6">Real Results</h2>
          <p className="text-xl text-gray-400 max-w-2xl">
            These are actual driveways we've sealed right here in Walworth County. Slide to compare before & after.
          </p>
        </div>

        {/* Before / After Slider */}
        <div className="mb-16">
          <h3 className="font-display text-3xl font-bold text-white uppercase mb-6 flex items-center gap-3">
            <span className="w-8 h-1 bg-orange-500 inline-block rounded"></span>
            Before & After
          </h3>
          <div
            className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden cursor-col-resize select-none"
            style={{ aspectRatio: '16/9' }}
            onMouseDown={() => setIsDragging(true)}
            onMouseUp={() => setIsDragging(false)}
            onMouseLeave={() => setIsDragging(false)}
            onMouseMove={handleMouseMove}
            onTouchMove={handleTouchMove}
          >
            {/* After (bottom layer — full width) */}
            <img src="/after.jpg" alt="After sealcoating" className="absolute inset-0 w-full h-full object-cover object-center" />

            {/* Before (top layer — clipped) */}
            <div className="absolute inset-0 overflow-hidden" style={{ width: `${sliderPos}%` }}>
              <img src="/before.jpg" alt="Before sealcoating" className="absolute inset-0 w-full h-full object-cover object-center" style={{ width: `${10000 / sliderPos}%`, maxWidth: 'none' }} />
            </div>

            {/* Divider line */}
            <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-10" style={{ left: `${sliderPos}%` }}>
              <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-xl flex items-center justify-center">
                <i className="ri-arrow-left-right-line text-gray-800 text-lg"></i>
              </div>
            </div>

            {/* Labels */}
            <div className="absolute top-4 left-4 bg-black/70 text-white text-sm font-bold px-3 py-1.5 rounded-full uppercase tracking-wide z-10">Before</div>
            <div className="absolute top-4 right-4 bg-orange-500 text-white text-sm font-bold px-3 py-1.5 rounded-full uppercase tracking-wide z-10">After</div>

            {/* Drag hint */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white/80 text-xs px-3 py-1 rounded-full z-10 pointer-events-none">
              ← Drag to compare →
            </div>
          </div>
        </div>

        {/* Work Photos Grid */}
        <h3 className="font-display text-3xl font-bold text-white uppercase mb-6 flex items-center gap-3">
          <span className="w-8 h-1 bg-orange-500 inline-block rounded"></span>
          Recent Jobs
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {workPhotos.map((photo, i) => (
            <div key={i} className="relative rounded-2xl overflow-hidden group" style={{ aspectRatio: '4/3' }}>
              <img
                src={photo.src}
                alt={photo.caption}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <p className="text-white text-sm font-medium">{photo.caption}</p>
              </div>
              <div className="absolute top-4 right-4 bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                Walworth Co.
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-6 text-lg">Want results like these for your driveway?</p>
          <a href="#contact" className="bg-orange-500 hover:bg-orange-600 text-white px-10 py-4 rounded-lg text-lg font-bold transition-all duration-200 inline-flex items-center gap-2 shadow-lg shadow-orange-500/20">
            <i className="ri-calculator-line"></i> Get Your Free Estimate
          </a>
        </div>
      </div>
    </section>
  );
}
